import { EnrollmentStatus, SelfEnrollType } from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import { Menu } from '@zorro/design-system/v2/Menu';
import { RecordForActions } from '@zorro/web/shared-ui';
import { useAppRouter } from '@zorro/web/ui-utils';

import { useBulkApplication } from '../../BulkApplication/BulkApplicationContext';
import { actionsMenuTooltipLabels } from '../ActionsMenuUtils';
import { ActionsMenuPrerequisitesTooltip } from '../ActionsRequirementTooltip';

type Props = {
  recordsForActions: RecordForActions[];
};

export const BulkApplicationMenuItem = ({ recordsForActions }: Props) => {
  const { navigateToRoute } = useAppRouter();
  const { setOnboardingPeriodIds } = useBulkApplication();

  if (recordsForActions.length === 0) {
    return null;
  }

  const [first] = recordsForActions;

  const isStateEqual = recordsForActions.every(
    ({ state }) => state === first.state
  );
  const isStartDateEqual = recordsForActions.every(
    ({ enrollmentStartDate }) =>
      enrollmentStartDate === first.enrollmentStartDate
  );
  const isCarrierEqual = recordsForActions.every(
    ({ carrier }) => carrier === first.carrier
  );
  const isEnrollmentStatus = recordsForActions.every(
    ({ enrollmentStatus }) =>
      enrollmentStatus === EnrollmentStatus.ELECTION_SUBMITTED
  );
  const isCombinedPlanEqual = recordsForActions.every(
    ({ isCombinedPlan }) => isCombinedPlan === first.isCombinedPlan
  );

  const allNotSelfEnroll = recordsForActions.every(
    ({ selfEnroll }) => selfEnroll !== SelfEnrollType.SELF_ENROLL
  );

  const isEqual =
    isStateEqual &&
    isStartDateEqual &&
    isCarrierEqual &&
    isEnrollmentStatus &&
    isCombinedPlanEqual &&
    allNotSelfEnroll;

  const handleButtonClick = () => {
    setOnboardingPeriodIds(
      recordsForActions
        .map(({ onboardingPeriodId }) => onboardingPeriodId)
        .filter(isDefined)
    );
    navigateToRoute('OPS_BULK_CARRIER_APPLICATION_ENROLLMENT_INFO', []);
  };

  return (
    <ActionsMenuPrerequisitesTooltip
      title="Bulk application"
      isAllEqual={isEqual}
      isInEnrollmentsDatatable
      prerequisites={[
        { isValid: isStateEqual, label: actionsMenuTooltipLabels.isStateEqual },
        {
          isValid: isStartDateEqual,
          label: actionsMenuTooltipLabels.isStartDateEqual,
        },
        {
          isValid: isCarrierEqual,
          label: actionsMenuTooltipLabels.isCarrierEqual,
        },
        {
          isValid: isEnrollmentStatus,
          label: actionsMenuTooltipLabels.isEnrollmentStatus,
        },
        {
          isValid: isCombinedPlanEqual,
          label: actionsMenuTooltipLabels.isCombinedPlanEqual,
        },
        {
          isValid: allNotSelfEnroll,
          label: actionsMenuTooltipLabels.isNotSelfEnroll,
        },
      ]}
    >
      <Menu.Item onClick={handleButtonClick} disabled={!isEqual}>
        Bulk application
      </Menu.Item>
    </ActionsMenuPrerequisitesTooltip>
  );
};
